import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, CalendarDays, MapPin, Clock, Loader2, Trash2, Pencil, X, Wallet, Inbox, Sparkles, Images, Phone, Mail, User, Compass, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import {
  adminListBookings, adminCreateBooking, adminUpdateBooking, adminDeleteBooking,
  adminAddPayment, adminDeletePayment, adminListPackages, adminListTeam, getServices, adminListScouts, adminCreateScout,
} from '@/lib/api';
import { formatCents, dollarsToCents, centsToDollarsInput, formatSessionDate, toISODate, fromISODate } from '@/lib/money';
import { ADMIN_BOOKINGS, SCOUT_ENTRY } from '@/constants/testIds';

export const BOOKING_STATUSES = ['requested', 'booked', 'shot', 'editing', 'delivered', 'cancelled'];
const PAYMENT_METHODS = ['cash', 'zelle', 'venmo', 'card', 'paypal', 'other'];
const NONE = '__none__';

const STATUS_STYLE = {
  requested: 'bg-amber-400/15 text-amber-300 border-amber-400/40',
  booked: 'bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))] border-[hsl(var(--lxra-gold))]/30',
  shot: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  editing: 'bg-violet-400/15 text-violet-300 border-violet-400/40',
  delivered: 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40',
  cancelled: 'bg-transparent text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]',
};

const PAY_STYLE = {
  paid: 'text-[hsl(152,46%,65%)]',
  deposit_paid: 'text-[hsl(var(--lxra-gold))]',
  partial: 'text-amber-300',
  unpaid: 'text-[hsl(var(--lxra-muted))]',
  no_charge: 'text-[hsl(var(--lxra-muted))]',
};
const PAY_LABEL = { paid: 'Paid in full', deposit_paid: 'Deposit paid', partial: 'Partially paid', unpaid: 'Unpaid', no_charge: 'No charge' };

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const selectContent = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]';

export const StatusBadge = ({ status }) => (
  <Badge className={`border capitalize hover:bg-transparent text-[10px] ${STATUS_STYLE[status] || STATUS_STYLE.booked}`}>{status}</Badge>
);

const EMPTY_FORM = {
  client_name: '', client_email: '', client_phone: '', package_id: NONE, service_slug: NONE, title: '',
  session_date: '', session_time: '', location: '', price: '', deposit: '', status: 'booked', photographer_email: NONE, notes: '', gallery_slug: '',
};

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------
function BookingFormDialog({ open, onOpenChange, booking, packages, services, team, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (booking) {
      setForm({
        client_name: booking.client_name || '',
        client_email: booking.client_email || '',
        client_phone: booking.client_phone || '',
        package_id: booking.package_id || NONE,
        service_slug: booking.service_slug || NONE,
        title: booking.title || '',
        session_date: booking.session_date || '',
        session_time: booking.session_time || '',
        location: booking.location || '',
        price: centsToDollarsInput(booking.total_price_cents),
        deposit: centsToDollarsInput(booking.deposit_cents),
        status: booking.status || 'booked',
        photographer_email: booking.photographer_email || NONE,
        notes: booking.notes || '',
        gallery_slug: booking.gallery_slug || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, booking]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const handlePackage = (pkgId) => {
    const pkg = packages.find((p) => p.id === pkgId);
    setForm((f) => ({
      ...f,
      package_id: pkgId,
      price: pkg ? centsToDollarsInput(pkg.price_cents) : f.price,
      deposit: pkg ? centsToDollarsInput(pkg.deposit_cents) : f.deposit,
      service_slug: pkg?.service_slug ? pkg.service_slug : f.service_slug,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      client_name: form.client_name.trim(),
      client_email: form.client_email.trim(),
      client_phone: form.client_phone.trim(),
      package_id: form.package_id === NONE ? null : form.package_id,
      service_slug: form.service_slug === NONE ? null : form.service_slug,
      title: form.title.trim(),
      session_date: form.session_date || null,
      session_time: form.session_time.trim(),
      location: form.location.trim(),
      total_price_cents: dollarsToCents(form.price),
      deposit_cents: dollarsToCents(form.deposit),
      status: form.status,
      photographer_email: form.photographer_email === NONE ? null : form.photographer_email,
      notes: form.notes,
      gallery_slug: form.gallery_slug.trim() || null,
    };
    try {
      const saved = booking ? await adminUpdateBooking(booking.id, payload) : await adminCreateBooking(payload);
      toast.success(booking ? 'Booking updated.' : 'Booking created.');
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the booking.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={ADMIN_BOOKINGS.dialog}>
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--lxra-fg))]">{booking ? 'Edit Booking' : 'New Booking'}</DialogTitle>
          <DialogDescription className="text-[hsl(var(--lxra-muted))]">
            Pick a package to pre-fill price and deposit — you can always override the numbers.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid={ADMIN_BOOKINGS.form}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Client name</Label>
              <Input required value={form.client_name} onChange={(e) => set('client_name')(e.target.value)} data-testid={ADMIN_BOOKINGS.nameInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Email</Label>
              <Input required type="email" value={form.client_email} onChange={(e) => set('client_email')(e.target.value)} data-testid={ADMIN_BOOKINGS.emailInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Phone</Label>
              <Input value={form.client_phone} onChange={(e) => set('client_phone')(e.target.value)} data-testid={ADMIN_BOOKINGS.phoneInput} className={fieldClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Package</Label>
              <Select value={form.package_id} onValueChange={handlePackage}>
                <SelectTrigger data-testid={ADMIN_BOOKINGS.packageSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContent}>
                  <SelectItem value={NONE}>Custom / no package</SelectItem>
                  {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatCents(p.price_cents)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Service</Label>
              <Select value={form.service_slug} onValueChange={set('service_slug')}>
                <SelectTrigger data-testid={ADMIN_BOOKINGS.serviceSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContent}>
                  <SelectItem value={NONE}>Not specified</SelectItem>
                  {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className={labelClass}>Title (optional)</Label>
            <Input value={form.title} onChange={(e) => set('title')(e.target.value)} className={fieldClass} placeholder="Auto-generated from client + package if left blank" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Session date</Label>
              <Input type="date" value={form.session_date} onChange={(e) => set('session_date')(e.target.value)} data-testid={ADMIN_BOOKINGS.dateInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Time</Label>
              <Input value={form.session_time} onChange={(e) => set('session_time')(e.target.value)} data-testid={ADMIN_BOOKINGS.timeInput} className={fieldClass} placeholder="5:30 PM" />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Status</Label>
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger data-testid={ADMIN_BOOKINGS.statusFormSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContent}>
                  {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className={labelClass}>Location</Label>
            <Input value={form.location} onChange={(e) => set('location')(e.target.value)} data-testid={ADMIN_BOOKINGS.locationInput} className={fieldClass} placeholder="Georgetown Waterfront, DC" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Total price ($)</Label>
              <Input inputMode="decimal" value={form.price} onChange={(e) => set('price')(e.target.value)} data-testid={ADMIN_BOOKINGS.priceInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Deposit ($)</Label>
              <Input inputMode="decimal" value={form.deposit} onChange={(e) => set('deposit')(e.target.value)} data-testid={ADMIN_BOOKINGS.depositInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Photographer</Label>
              <Select value={form.photographer_email} onValueChange={set('photographer_email')}>
                <SelectTrigger data-testid={ADMIN_BOOKINGS.photographerSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContent}>
                  <SelectItem value={NONE}>Unassigned</SelectItem>
                  {team.map((m) => <SelectItem key={m.email} value={m.email}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className={labelClass}>Internal notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => set('notes')(e.target.value)} data-testid={ADMIN_BOOKINGS.notesTextarea} className={`${fieldClass} h-auto resize-y`} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Linked gallery slug (optional)</Label>
              <Input value={form.gallery_slug} onChange={(e) => set('gallery_slug')(e.target.value)} className={fieldClass} placeholder="private-preview" />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid={ADMIN_BOOKINGS.saveButton} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : booking ? 'Save Changes' : 'Create Booking'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Detail dialog: everything about one booking + track-only payments ledger
// ---------------------------------------------------------------------------
function BookingDetailDialog({ booking, onClose, onEdit, onChanged, onDeleted }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('zelle');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [scouts, setScouts] = useState([]);
  const [scouting, setScouting] = useState(false);

  useEffect(() => {
    if (booking?.id) adminListScouts({ booking_id: booking.id }).then(setScouts).catch(() => setScouts([]));
  }, [booking?.id]);

  const runScout = async () => {
    if (!booking.location) return toast.error('Add a location to this booking first.');
    if (!booking.session_date) return toast.error('Set a session date first.');
    setScouting(true);
    try {
      const doc = await adminCreateScout({
        location: booking.location,
        date: booking.session_date,
        time: booking.session_time || '',
        occasion: booking.package_name || booking.title || '',
        service_slug: booking.service_slug || null,
        subjects: booking.client_name,
        notes: booking.client_notes || '',
        booking_id: booking.id,
        blueprint_id: booking.blueprint_id || null,
        generate_image: true,
      });
      toast.success('Scouting the location…');
      navigate(`/admin/scout/${doc.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the scout.');
    } finally {
      setScouting(false);
    }
  };

  const bookingId = booking?.id;
  const paidCents = booking?.amount_paid_cents;
  const depositCents = booking?.deposit_cents;
  const balanceCents = booking?.balance_cents;
  useEffect(() => {
    if (!bookingId) return;
    // Suggest the next sensible amount: deposit if nothing paid, else the balance.
    const suggestion = paidCents === 0 && depositCents ? depositCents : balanceCents;
    setAmount(suggestion ? centsToDollarsInput(suggestion) : '');
    setNote('');
  }, [bookingId, paidCents, depositCents, balanceCents]);

  if (!booking) return null;

  const addPayment = async (e) => {
    e.preventDefault();
    const cents = dollarsToCents(amount);
    if (cents <= 0) {
      toast.error('Enter an amount greater than zero.');
      return;
    }
    setBusy(true);
    try {
      const updated = await adminAddPayment(booking.id, { amount_cents: cents, method, note: note.trim() });
      onChanged(updated);
      toast.success(`Recorded ${formatCents(cents)} via ${method}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record the payment.');
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (pid) => {
    if (!window.confirm('Remove this payment record?')) return;
    try {
      const updated = await adminDeletePayment(booking.id, pid);
      onChanged(updated);
      toast.success('Payment removed.');
    } catch {
      toast.error('Could not remove the payment.');
    }
  };

  const changeStatus = async (status) => {
    try {
      const updated = await adminUpdateBooking(booking.id, { status });
      onChanged(updated);
      if (updated.confirmation_email_status === 'sent') toast.success('Booking confirmed and the client has been emailed.');
      else if (updated.confirmation_email_status) toast.success('Booking confirmed. (Client email skipped — SendGrid not configured.)');
      else toast.success(`Marked ${status}.`);
    } catch {
      toast.error('Could not update status.');
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this booking and its payment history?')) return;
    try {
      await adminDeleteBooking(booking.id);
      toast.success('Booking deleted.');
      onDeleted(booking.id);
    } catch {
      toast.error('Could not delete the booking.');
    }
  };

  const payments = [...(booking.payments || [])].sort((a, b) => (a.paid_at < b.paid_at ? 1 : -1));
  const progress = booking.total_price_cents > 0 ? Math.min(100, Math.round((booking.amount_paid_cents / booking.total_price_cents) * 100)) : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={ADMIN_BOOKINGS.detail}>
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={booking.status} />
            <span className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">via {booking.source}</span>
          </div>
          <DialogTitle className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]" data-testid={ADMIN_BOOKINGS.detailTitle}>
            {booking.title || booking.client_name}
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--lxra-muted))]">
            {booking.package_name || 'Custom booking'}{booking.duration_label ? ` · ${booking.duration_label}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-[hsl(var(--lxra-fg))]"><User className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> {booking.client_name}</p>
              <p className="flex items-center gap-2 text-[hsl(var(--lxra-muted))] text-xs"><Mail className="h-3.5 w-3.5" /> {booking.client_email}</p>
              {booking.client_phone && <p className="flex items-center gap-2 text-[hsl(var(--lxra-muted))] text-xs"><Phone className="h-3.5 w-3.5" /> {booking.client_phone}</p>}
            </div>
            <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
                <CalendarDays className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" />
                {formatSessionDate(booking.session_date) || 'Date to be confirmed'}
              </p>
              {booking.session_time && <p className="flex items-center gap-2 text-[hsl(var(--lxra-muted))] text-xs"><Clock className="h-3.5 w-3.5" /> {booking.session_time}</p>}
              {booking.alt_date && <p className="text-xs text-[hsl(var(--lxra-muted))] pl-5">Alt: {formatSessionDate(booking.alt_date)}</p>}
              {booking.location && <p className="flex items-center gap-2 text-[hsl(var(--lxra-muted))] text-xs"><MapPin className="h-3.5 w-3.5" /> {booking.location}</p>}
              {booking.photographer_email && <p className="text-xs text-[hsl(var(--lxra-muted))] pl-5">Photographer: {booking.photographer_email}</p>}
            </div>
            <div className="rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/[0.05] p-4 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] flex items-center gap-1.5"><Compass className="h-3.5 w-3.5" /> Location Scout</p>
              {scouts.length > 0 ? (
                <ul className="space-y-1">
                  {scouts.slice(0, 3).map((s) => (
                    <li key={s.id}>
                      <Link to={`/admin/scout/${s.id}`} data-testid={SCOUT_ENTRY.bookingScoutLink} className="text-xs text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] inline-flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${s.status === 'ready' ? 'bg-emerald-400' : s.status === 'failed' ? 'bg-red-400' : 'bg-[hsl(var(--lxra-gold))] animate-pulse'}`} />
                        {s.place?.name || s.request?.location} · {s.request?.time || 'open'} <ExternalLink className="h-3 w-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-[hsl(var(--lxra-muted))]">Sun, light, positions and settings for this shoot — one click.</p>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={runScout}
                disabled={scouting}
                data-testid={SCOUT_ENTRY.bookingButton}
                className="h-8 border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 hover:text-[hsl(var(--lxra-gold-hover))] text-[11px] uppercase tracking-[0.08em]"
              >
                {scouting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Compass className="h-3.5 w-3.5 mr-1.5" /> {scouts.length ? 'Scout again' : 'Scout this location'}</>}
              </Button>
            </div>
            {booking.client_notes && (
              <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4">
                <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] mb-1">From the client</p>
                <p className="text-sm text-[hsl(var(--lxra-fg))] whitespace-pre-wrap">{booking.client_notes}</p>
              </div>
            )}
            {booking.notes && (
              <div className="rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[#141416] p-4">
                <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] mb-1">Internal notes</p>
                <p className="text-sm text-[hsl(var(--lxra-fg))] whitespace-pre-wrap">{booking.notes}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {booking.gallery_slug && (
                <Link to={`/galleries/${booking.gallery_slug}`} target="_blank" className="inline-flex items-center h-8 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors">
                  <Images className="h-3.5 w-3.5 mr-1.5" /> Gallery
                </Link>
              )}
              {booking.blueprint_id && (
                <Link to={`/plan/${booking.blueprint_id}`} target="_blank" className="inline-flex items-center h-8 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Blueprint
                </Link>
              )}
            </div>
          </div>

          {/* Right: money + status */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Total</p>
                  <p className="font-display text-2xl text-[hsl(var(--lxra-fg))]">{formatCents(booking.total_price_cents)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Balance</p>
                  <p className="font-display text-2xl text-[hsl(var(--lxra-gold))]" data-testid={ADMIN_BOOKINGS.detailBalance}>{formatCents(booking.balance_cents)}</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 w-full rounded-full bg-[#0F0F10] overflow-hidden">
                <div className="h-full bg-[hsl(var(--lxra-gold))] transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className={`mt-2 text-xs ${PAY_STYLE[booking.payment_status]}`} data-testid={ADMIN_BOOKINGS.detailPaid}>
                {PAY_LABEL[booking.payment_status]} · {formatCents(booking.amount_paid_cents)} received{booking.deposit_cents ? ` · deposit ${formatCents(booking.deposit_cents)}` : ''}
              </p>
              {booking.studio_id !== 'lxra' && booking.platform_fee_cents > 0 && (
                <p className="mt-1 text-[11px] text-[hsl(var(--lxra-muted))]">Your net so far <span className="text-[hsl(var(--lxra-fg))]">{formatCents(booking.photographer_net_cents)}</span> · LXRA fee <span className="text-[hsl(var(--lxra-gold))]">{formatCents(booking.platform_fee_cents)}</span></p>
              )}
            </div>

            <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4">
              <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] mb-3 flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Record a payment</p>
              <form onSubmit={addPayment} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-2">
                <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount ($)" data-testid={ADMIN_BOOKINGS.paymentAmountInput} className={`${fieldClass} h-9`} />
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid={ADMIN_BOOKINGS.paymentMethodSelect} className={`${fieldClass} h-9`}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContent}>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. retainer)" data-testid={ADMIN_BOOKINGS.paymentNoteInput} className={`${fieldClass} h-9`} />
                <Button type="submit" disabled={busy} data-testid={ADMIN_BOOKINGS.paymentAddButton} className="h-9 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.08em] font-semibold disabled:opacity-60">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
                </Button>
              </form>
              <div className="mt-3 space-y-1.5" data-testid={ADMIN_BOOKINGS.paymentsList}>
                {payments.length === 0 && <p className="text-xs text-[hsl(var(--lxra-muted))]">No payments recorded yet.</p>}
                {payments.map((p) => (
                  <div key={p.id} data-testid={ADMIN_BOOKINGS.paymentRow(p.id)} className="flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-3 py-2 text-xs">
                    <div className="min-w-0">
                      <span className="text-[hsl(var(--lxra-fg))] font-medium">{formatCents(p.amount_cents)}</span>
                      <span className="text-[hsl(var(--lxra-muted))] capitalize"> · {p.method}</span>
                      {p.note && <span className="text-[hsl(var(--lxra-muted))]"> · {p.note}</span>}
                      {booking.studio_id !== 'lxra' && p.platform_fee_cents > 0 && (
                        <span className="text-[hsl(var(--lxra-muted))]"> · fee {formatCents(p.platform_fee_cents)}</span>
                      )}
                      <p className="text-[hsl(var(--lxra-muted))]">{new Date(p.paid_at).toLocaleDateString()}</p>
                    </div>
                    <button type="button" onClick={() => removePayment(p.id)} data-testid={ADMIN_BOOKINGS.paymentDelete(p.id)} className="text-[hsl(var(--lxra-muted))] hover:text-red-400 transition-colors" aria-label="Remove payment">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Select value={booking.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid={ADMIN_BOOKINGS.statusSelect(booking.id)} className={`${fieldClass} h-9 w-40 text-xs capitalize`}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContent}>
                  {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={onEdit} data-testid={ADMIN_BOOKINGS.editButton} className="h-9 px-3 border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] hover:bg-transparent text-xs uppercase tracking-[0.08em]">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
              <Button variant="ghost" onClick={remove} data-testid={ADMIN_BOOKINGS.deleteButton} className="ml-auto h-9 px-3 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10 text-xs uppercase tracking-[0.08em]">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------
export const AdminBookingsTab = () => {
  const [bookings, setBookings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedDay, setSelectedDay] = useState(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const refresh = useCallback(() => adminListBookings().then(setBookings).catch(() => setBookings([])), []);

  useEffect(() => {
    refresh();
    adminListPackages().then((p) => setPackages(p.filter((x) => x.active))).catch(() => setPackages([]));
    getServices().then(setServices).catch(() => setServices([]));
    adminListTeam().then(setTeam).catch(() => setTeam([]));
  }, [refresh]);

  const upsert = (saved) => setBookings((prev) => {
    if (!prev) return [saved];
    const exists = prev.some((b) => b.id === saved.id);
    return exists ? prev.map((b) => (b.id === saved.id ? saved : b)) : [...prev, saved];
  });

  const sessionDays = useMemo(
    () => (bookings || []).filter((b) => b.session_date && b.status !== 'cancelled').map((b) => fromISODate(b.session_date)),
    [bookings],
  );
  const requestedDays = useMemo(
    () => (bookings || []).filter((b) => b.session_date && b.status === 'requested').map((b) => fromISODate(b.session_date)),
    [bookings],
  );

  const visible = useMemo(() => {
    if (!bookings) return null;
    const q = query.trim().toLowerCase();
    const day = selectedDay ? toISODate(selectedDay) : null;
    return bookings.filter((b) => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (day && b.session_date !== day) return false;
      if (q && ![b.client_name, b.client_email, b.title, b.location, b.package_name].some((v) => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [bookings, statusFilter, query, selectedDay]);

  const requestedCount = (bookings || []).filter((b) => b.status === 'requested').length;
  const detail = detailId ? (bookings || []).find((b) => b.id === detailId) : null;

  const quickStatus = async (booking, status) => {
    try {
      const updated = await adminUpdateBooking(booking.id, { status });
      upsert(updated);
      if (updated.confirmation_email_status) toast.success(updated.confirmation_email_status === 'sent' ? 'Confirmed — client emailed.' : 'Confirmed. (Client email skipped — SendGrid not configured.)');
      else toast.success(`Marked ${status}.`);
    } catch {
      toast.error('Could not update status.');
    }
  };

  return (
    <div className="space-y-6" data-testid={ADMIN_BOOKINGS.section}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Creator</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Bookings &amp; Projects</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Every shoot from first request to delivery — with what&apos;s been paid along the way.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          data-testid={ADMIN_BOOKINGS.newButton}
          className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Booking
        </Button>
      </div>

      {requestedCount > 0 && statusFilter === 'all' && (
        <div data-testid={ADMIN_BOOKINGS.requestedBanner} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] px-5 py-3.5">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-400/40 text-amber-400 shrink-0"><Inbox className="h-4 w-4" /></span>
            <p className="text-sm text-[hsl(var(--lxra-fg))]"><strong className="text-amber-400">{requestedCount}</strong> booking request{requestedCount === 1 ? '' : 's'} waiting for you to confirm a date.</p>
          </div>
          <Button variant="outline" onClick={() => setStatusFilter('requested')} className="h-8 px-3 border-amber-400/40 text-amber-400 hover:bg-amber-400/10 hover:text-amber-300 text-xs uppercase tracking-[0.08em] font-semibold">Review requests</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
        {/* Calendar */}
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-3" data-testid={ADMIN_BOOKINGS.calendar}>
          <Calendar
            mode="single"
            selected={selectedDay}
            onSelect={setSelectedDay}
            modifiers={{ session: sessionDays, requested: requestedDays }}
            modifiersClassNames={{
              session: 'lxra-day-session',
              requested: 'lxra-day-requested',
            }}
            className="text-[hsl(var(--lxra-fg))]"
            classNames={{
              day_selected: 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold))] hover:text-[#0A0A0A] focus:bg-[hsl(var(--lxra-gold))] focus:text-[#0A0A0A]',
              day_today: 'border border-[hsl(var(--lxra-gold))]/50',
              head_cell: 'text-[hsl(var(--lxra-muted))] rounded-md w-9 font-normal text-[0.7rem] uppercase tracking-wider',
              caption_label: 'text-sm font-medium text-[hsl(var(--lxra-fg))]',
              nav_button: 'h-7 w-7 bg-transparent border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] rounded-md inline-flex items-center justify-center',
              day: 'h-9 w-9 p-0 font-normal rounded-md hover:bg-[#1a1a1c] aria-selected:opacity-100 relative',
              day_outside: 'text-[hsl(var(--lxra-muted))] opacity-40',
            }}
          />
          <div className="flex items-center justify-between px-2 pb-1 pt-2 text-[10px] text-[hsl(var(--lxra-muted))]">
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--lxra-gold))]" /> Session</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Requested</span>
            {selectedDay && (
              <button type="button" onClick={() => setSelectedDay(undefined)} data-testid={ADMIN_BOOKINGS.clearDateButton} className="inline-flex items-center gap-1 text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))]">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--lxra-muted))]" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search client, title, location…" data-testid={ADMIN_BOOKINGS.searchInput} className={`${fieldClass} h-9 pl-9 text-xs`} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid={ADMIN_BOOKINGS.statusFilter} className={`${fieldClass} h-9 w-40 text-xs capitalize`}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContent}>
                <SelectItem value="all">All statuses</SelectItem>
                {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selectedDay && (
            <p className="text-xs text-[hsl(var(--lxra-muted))]">Showing {formatSessionDate(toISODate(selectedDay))}</p>
          )}

          {!visible ? (
            <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
          ) : visible.length === 0 ? (
            <div data-testid={ADMIN_BOOKINGS.empty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
              <CalendarDays className="h-7 w-7 mx-auto text-[hsl(var(--lxra-gold))]" />
              <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))]">{bookings.length === 0 ? 'No bookings yet.' : 'Nothing matches these filters.'}</p>
              <p className="mt-1 text-xs text-[hsl(var(--lxra-muted))]">{bookings.length === 0 ? 'Create one manually or share /book so clients can request a date.' : 'Try clearing the date or status filter.'}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid={ADMIN_BOOKINGS.list}>
              {visible.map((b) => (
                <div
                  key={b.id}
                  data-testid={ADMIN_BOOKINGS.row(b.id)}
                  className={`group grid grid-cols-[auto_1fr_auto] gap-4 items-center rounded-xl border bg-[#0F0F10] px-4 py-3 hover:bg-[#141416] transition-colors ${b.status === 'requested' ? 'border-amber-400/30' : 'border-[hsl(var(--lxra-border))]'}`}
                >
                  <button type="button" onClick={() => setDetailId(b.id)} data-testid={ADMIN_BOOKINGS.openButton(b.id)} className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-[#141416] border border-[hsl(var(--lxra-border))] group-hover:border-[hsl(var(--lxra-gold))]/40 text-center">
                    {b.session_date ? (
                      <>
                        <span className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))]">{formatSessionDate(b.session_date, { month: 'short' })}</span>
                        <span className="font-display text-xl leading-none text-[hsl(var(--lxra-fg))]">{formatSessionDate(b.session_date, { day: 'numeric' })}</span>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] leading-tight">TBD</span>
                    )}
                  </button>
                  <button type="button" onClick={() => setDetailId(b.id)} className="min-w-0 text-left">
                    <p className="text-sm text-[hsl(var(--lxra-fg))] truncate">{b.title || b.client_name}</p>
                    <p className="text-xs text-[hsl(var(--lxra-muted))] truncate">
                      {b.client_name} · {b.package_name || 'Custom'}{b.session_time ? ` · ${b.session_time}` : ''}{b.location ? ` · ${b.location}` : ''}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${PAY_STYLE[b.payment_status]}`}>
                      {formatCents(b.total_price_cents)} · {PAY_LABEL[b.payment_status]}{b.balance_cents > 0 && b.total_price_cents > 0 ? ` · ${formatCents(b.balance_cents)} due` : ''}
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <Select value={b.status} onValueChange={(v) => quickStatus(b, v)}>
                      <SelectTrigger data-testid={ADMIN_BOOKINGS.statusSelect(b.id)} className="h-8 w-[118px] bg-transparent border-0 p-0 focus:ring-0 justify-end gap-1">
                        <SelectValue><StatusBadge status={b.status} /></SelectValue>
                      </SelectTrigger>
                      <SelectContent className={selectContent}>
                        {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BookingFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        booking={editing}
        packages={packages}
        services={services}
        team={team}
        onSaved={(saved) => { upsert(saved); if (editing) setDetailId(saved.id); }}
      />

      {detail && !formOpen && (
        <BookingDetailDialog
          booking={detail}
          onClose={() => setDetailId(null)}
          onEdit={() => { setEditing(detail); setFormOpen(true); }}
          onChanged={upsert}
          onDeleted={(id) => { setBookings((prev) => prev.filter((b) => b.id !== id)); setDetailId(null); }}
        />
      )}
    </div>
  );
};
